import type { Question } from "../types";

/** A run of questions under one title; `title` is empty for the ungrouped ones. */
export type QuestionSection<T> = { title: string; items: T[] };

/**
 * Items (questions, or anything carrying one) gathered by their question's group,
 * each group where its first question comes — the order the server stores
 * (`orderByGroup`). One untitled section when nothing is grouped.
 */
export function sectionsOf<T>(
  items: T[],
  groupOf: (item: T) => string | undefined,
): QuestionSection<T>[] {
  const sections: QuestionSection<T>[] = [];
  const byTitle = new Map<string, QuestionSection<T>>();
  for (const item of items) {
    const title = groupOf(item)?.trim() || "";
    let section = byTitle.get(title);
    if (!section) {
      section = { title, items: [] };
      byTitle.set(title, section);
      sections.push(section);
    }
    section.items.push(item);
  }
  return sections;
}

/** The questions with each group in one block, as `sectionsOf` lists them. */
export function orderByGroup(questions: Question[]): Question[] {
  return sectionsOf(questions, (q) => q.group).flatMap((s) => s.items);
}

/** The group titles in use, in order. */
export function groupTitles(questions: Question[]): string[] {
  return sectionsOf(questions, (q) => q.group)
    .map((s) => s.title)
    .filter(Boolean);
}
